import React, { Suspense } from 'react';
import { connect } from 'react-redux';
import { reduxForm, change } from 'redux-form';
import styled from 'styled-components';

import If from 'components/Shared/If';
import SettingsGroup from 'components/Shared/SettingsGroup';
import { ContentSet, FlexRow } from 'components/Shared/Scaffolding';
import { TextButton } from 'components/Shared/Buttons';
import { Select, ButtonSet } from 'components/Shared/Forms';
import { saveAdvancedSettings, getAdvancedSettings, getLanguage, mapTypes } from 'modules/advancedSettings';
import { getMapTileLanguage, languageMismatch } from 'store';
import ControlsSection from 'components/AdvancedSettings/ControlsSection';
import BehaviorSection from 'components/AdvancedSettings/BehaviorSection';
import { BREAKPOINTS, SCAFFOLDING, TYPOGRAPHY } from 'theme';
import { LanguagesSelect } from './LanguagesSelect';
import { SmallLoading } from 'components/Loading/SmallLoading';
import { useRecoilValue } from 'recoil';
import { languageNameByCodeSelector } from 'modules/languages';

const StyledSelect = styled(Select)`
    min-width: 15em;

    @media (min-width: ${`${BREAKPOINTS.xxl}px`}) {
        width: calc(50% - ${SCAFFOLDING.GUTTER_FACTOR(0.25)});
    }
`;

const StyledFlexRow = styled(FlexRow)`
    margin-top: ${SCAFFOLDING.GUTTER_FACTOR(0.25)};

    > * {
        display: inline-block;
    }
`;

const HighlightText = styled.span`
    flex: 0 0 auto;
    color: ${TYPOGRAPHY.COLOR_DANGER};
`;

const AdvancedForm = ({ handleSubmit, mapTileLanguage, language, languageMismatch, cancelPanel }) => {
    const mapTypeOptions = Object.values(mapTypes).map((value) => ({
        label: value.charAt(0).toUpperCase() + value.slice(1),
        value,
    }));
    const mapTileLanguageName = useRecoilValue(languageNameByCodeSelector(mapTileLanguage));
    const languageName = useRecoilValue(languageNameByCodeSelector(language));
    return (
        <form onSubmit={handleSubmit}>
            <ContentSet spacing={SCAFFOLDING.GUTTER}>
                <SettingsGroup name="Language" description="Specify the language to use on your map">
                    <Suspense fallback={<SmallLoading />}>
                        <LanguagesSelect name="language" />
                    </Suspense>
                    <If statement={languageMismatch}>
                        <StyledFlexRow spacing="4px">
                            <HighlightText>**</HighlightText>
                            <em>
                                The map is currently in <strong>{mapTileLanguageName}</strong>. Refresh this page to set
                                the map to <strong>{languageName}</strong>. Your changes will not be lost.
                            </em>
                        </StyledFlexRow>
                    </If>
                </SettingsGroup>
                <SettingsGroup name="Map Type" description="Select what map type should be displayed by default">
                    <StyledSelect name="mapType" options={mapTypeOptions} />
                </SettingsGroup>
                <SettingsGroup name="Controls" description="Specify which controls should be displayed on your map">
                    <ControlsSection />
                </SettingsGroup>
                <SettingsGroup name="Behavior" description="Specify advanced behavior settings for your map">
                    <BehaviorSection />
                </SettingsGroup>
                <ButtonSet>
                    <TextButton primary type="submit">
                        Apply Changes
                    </TextButton>
                    <TextButton type="button" onClick={cancelPanel}>
                        Cancel
                    </TextButton>
                </ButtonSet>
            </ContentSet>
        </form>
    );
};

const mapStateToProps = (state) => ({
    mapTileLanguage: getMapTileLanguage(),
    language: getLanguage(state),
    languageMismatch: languageMismatch(state),
    initialValues: {
        ...getAdvancedSettings(state),
        language: getLanguage(state),
    },
});

const mapDispatchToProps = () => ({
    onSubmit: (values, dispatch) => {
        dispatch(saveAdvancedSettings(values));
    },
    onSubmitSuccess: (result, dispatch, props) => {
        props.closePanel();
    },
});

const form = 'advanced-form';

export const setMapType = (mapType) => change(form, 'mapType', mapType);

export default connect(mapStateToProps, mapDispatchToProps)(reduxForm({ form })(AdvancedForm));
